import React, { useEffect } from "react";
import "@/App.css";
import "@/styles/embed.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { initEmbedContext } from "@/lib/embed-context";
import CompactBriefOverlay from "@/components/embed/CompactBriefOverlay";

// Initialise the ?embed=widget body-attribute gate before React renders
// any chrome (TableHeader, footers, Concierge® pills). The function is a
// no-op when ?embed=widget is absent. See /app/frontend/src/styles/embed.css
// for the CSS rules that hide chrome when the body attribute is set.
initEmbedContext();

// Vanity hostnames that mirror the /club portal as their default landing page.
// Visiting `kvb.mindescapes.in/` redirects to `/club` so members never see
// the marketing homepage. Everything else on the host (including /club/admin,
// /mira/console, etc.) keeps working unchanged.
const CLUB_MIRROR_HOSTS = new Set(["kvb.mindescapes.in"]);

function MirrorRoot() {
    if (typeof window !== "undefined" && CLUB_MIRROR_HOSTS.has(window.location.hostname)) {
        return <Navigate to="/club" replace />;
    }
    return <PublicShell><Home /></PublicShell>;
}

import Layout from "@/components/Layout";
import { ThemeProvider } from "@/components/ThemeProvider";
import Home from "@/pages/Home";
import About from "@/pages/About";
import { SolutionsIndex, SolutionDetail } from "@/pages/Solutions";
import Connect from "@/pages/Connect";
import MiraPage from "@/pages/Mira";
import TablePage from "@/pages/Table";
import EnjoyPage from "@/pages/Enjoy";
import EnjoySignature from "@/pages/EnjoySignature";
import EscapePage from "@/pages/Escape";
import Cadence from "@/pages/Cadence";
import StayPage from "@/pages/Stay";
import FitPage from "@/pages/Fit";
import Walks from "@/pages/Walks";
import CarePage from "@/pages/Care";
import ErrandsPage from "@/pages/Errands";
import AnythingPage from "@/pages/Anything";
import GoldenBookPage from "@/pages/GoldenBook";
import DreamweaverPage from "@/pages/Dreamweaver";
import DreamweaverEmbedPage from "@/pages/DreamweaverEmbed";
import AdminMemberContext from "@/pages/AdminMemberContext";
import MaisonPage from "@/pages/Maison";
import LegalPage from "@/pages/Legal";
import TermsPage from "@/pages/Terms";
import AdminDreamweaverPage from "@/pages/AdminDreamweaver";
import AirportPage from "@/pages/Airport";
import MobilityPage from "@/pages/Mobility";
import AdminPhotosPage from "@/pages/AdminPhotos";
import EscapeDestinationPage from "@/pages/EscapeDestination";
import TableCurate from "@/pages/TableCurate";
import TableDesk from "@/pages/TableDesk";
import TableAdmin from "@/pages/TableAdmin";
import TableAdminConfirmQueue from "@/pages/TableAdminConfirmQueue";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
// Concierge OS Admin · Phase 1a · the Control Room shell (14 Jun 2026).
// Replaces the flat /admin Editorial desk with a role-aware grouped
// command room. The /admin/legacy route is preserved as a one-step
// escape hatch in case any migrated panel needs to be opened in its
// original flat layout while we prove migration parity.
import ConciergeOSShell from "@/pages/admin/os/ConciergeOSShell";
import ControlRoom from "@/pages/admin/os/ControlRoom";
import MigrationMap from "@/pages/admin/os/MigrationMap";
import RoleAdmin from "@/pages/admin/os/RoleAdmin";
import Placeholder from "@/pages/admin/os/Placeholder";
import RequestsQueue from "@/pages/admin/os/RequestsQueue";
import AnythingQueue from "@/pages/admin/os/operate/AnythingQueue";
import GoldenBookNotes from "@/pages/admin/os/operate/GoldenBookNotes";
import GoldenBookQueue from "@/pages/admin/os/operate/GoldenBookQueue";
import FyiQueue from "@/pages/admin/os/FyiQueue";
import PublishGate from "@/pages/admin/os/PublishGate";
import EnjoySignatureCRUD from "@/pages/admin/os/EnjoySignatureCRUD";
import EnjoyOverlayCRUD from "@/pages/admin/os/EnjoyOverlayCRUD";
import TableSignatureCRUD from "@/pages/admin/os/TableSignatureCRUD";
import EscapeSignatureCRUD from "@/pages/admin/os/EscapeSignatureCRUD";
import MiraInboxPage from "@/pages/admin/os/operate/MiraInboxPage";
import EnquiriesPage from "@/pages/admin/os/operate/EnquiriesPage";
import TableModule from "@/pages/admin/os/modules/TableModule";
import EnjoyModule from "@/pages/admin/os/modules/EnjoyModule";
import EscapeModule from "@/pages/admin/os/modules/EscapeModule";
import ClubModule from "@/pages/admin/os/modules/ClubModule";
import StayModule from "@/pages/admin/os/modules/StayModule";
import FitModule from "@/pages/admin/os/modules/FitModule";
import AirportModule from "@/pages/admin/os/modules/AirportModule";
import MobilityModule from "@/pages/admin/os/modules/MobilityModule";
import ExclusiveModule from "@/pages/admin/os/modules/ExclusiveModule";
import SecretsExportPage from "@/pages/admin/SecretsExportPage";
import MembersPage from "@/pages/admin/os/operations/MembersPage";
import AurelionRoutingPage from "@/pages/admin/os/operations/AurelionRoutingPage";
import SiteContentPage from "@/pages/admin/os/system/SiteContentPage";
import DoctrineDocsPage from "@/pages/admin/os/system/DoctrineDocsPage";
import AdminUrlsPage from "@/pages/admin/os/system/AdminUrlsPage";
import WebhookLogPage from "@/pages/admin/os/system/WebhookLogPage";
import SiteVaultDownloads from "@/pages/admin/os/system/SiteVaultDownloads";
import ConsoleLogin from "@/pages/console/ConsoleLogin";
import ConsoleDashboard from "@/pages/console/ConsoleDashboard";
import ClubDirectory from "@/pages/club/ClubDirectory";
import ClubSignup from "@/pages/club/ClubSignup";
import ClubAdminRequests from "@/pages/club/ClubAdminRequests";
import { ClubAdminClubsList, ClubAdminClubEdit } from "@/pages/club/ClubAdminClubs";
import { ClubAdminMembersList, ClubAdminMemberEdit } from "@/pages/club/ClubAdminMembers";
// Corporate Sponsor Tenant portal (04 Jul 2026) · lesconcierge.com/{slug}
import CorporateLanding   from "@/pages/corporate/Landing";
import CorporateSignIn    from "@/pages/corporate/SignIn";
import CorporateTerms     from "@/pages/corporate/Terms";
import CorporateDashboard from "@/pages/corporate/Dashboard";
import AdminCorporateTenants from "@/pages/corporate/AdminTenants";
import { ContentProvider } from "@/lib/content";
import { AuthProvider } from "@/lib/auth";
import { ConsoleAuthProvider } from "@/lib/console-auth";
import { Toaster } from "@/components/ui/sonner";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function PublicShell({ children }) {
    return <Layout>{children}</Layout>;
}

function AdminShell({ children }) {
    return <Layout>{children}</Layout>;
}

function ConsoleShell({ children }) {
    // The Console intentionally renders WITHOUT the marketing Layout — it is
    // a fully separate dark workspace with its own chrome.
    return <>{children}</>;
}

function App() {
    return (
        <ContentProvider>
            <AuthProvider>
                <ConsoleAuthProvider>
                    <ThemeProvider>
                        <BrowserRouter>
                        <ScrollToTop />
                        <CompactBriefOverlay />
                        <Toaster position="bottom-center" richColors closeButton />
                        <Routes>
                            <Route path="/" element={<PublicShell><Home /></PublicShell>} />
                            <Route path="/about" element={<PublicShell><About /></PublicShell>} />
                            <Route path="/solutions" element={<PublicShell><SolutionsIndex /></PublicShell>} />
                            <Route path="/solutions/:slug" element={<PublicShell><SolutionDetail /></PublicShell>} />
                            <Route path="/connect" element={<PublicShell><Connect /></PublicShell>} />
                            <Route path="/mira" element={<MiraPage />} />
                            <Route path="/table" element={<TablePage />} />
                            <Route path="/enjoy" element={<EnjoyPage />} />
                            <Route path="/enjoy/signature" element={<EnjoySignature />} />
                            <Route path="/escape" element={<EscapePage />} />
                            <Route path="/cadence" element={<Cadence />} />
                            <Route path="/stay" element={<StayPage />} />
                            <Route path="/fit" element={<FitPage />} />
                            <Route path="/fit/walks" element={<Walks />} />
                            <Route path="/fit/walks/saved/:ref" element={<Walks />} />
                            <Route path="/fit/walks/mine" element={<Walks />} />
                            <Route path="/walks" element={<Walks />} />
                            <Route path="/care" element={<CarePage />} />
                            <Route path="/errands" element={<ErrandsPage />} />
                            <Route path="/anything" element={<AnythingPage />} />
                            <Route path="/goldenbook" element={<GoldenBookPage />} />
                            <Route path="/dreamweaver" element={<DreamweaverPage />} />
                            <Route path="/dreamweaver/embed" element={<DreamweaverEmbedPage />} />
                            <Route path="/admin/member-context/:email" element={<AdminMemberContext />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/admin/dreamweaver" element={<AdminDreamweaverPage />} />
                            <Route path="/exclusive" element={<MaisonPage />} />
                            <Route path="/legal" element={<LegalPage />} />
                            <Route path="/airport" element={<AirportPage />} />
                            <Route path="/mobility" element={<MobilityPage />} />
                            <Route path="/admin/photos" element={<AdminPhotosPage />} />
                            <Route path="/escape/destination/:slug" element={<EscapeDestinationPage />} />
                            <Route path="/table/curate" element={<TableCurate />} />
                            <Route path="/table/desk" element={<TableDesk />} />
                            <Route path="/table/admin" element={<TableAdmin />} />
                            <Route path="/table/admin/confirm-queue" element={<TableAdminConfirmQueue />} />
                            <Route path="/admin/login" element={<AdminShell><AdminLogin /></AdminShell>} />
                            {/* Concierge OS Admin · Phase 1a · 14 Jun 2026.
                                Nested routes — the shell renders <Outlet/>
                                and the grouped sidebar resolves every nav
                                item to a real page (no dead links). */}
                            <Route path="/admin" element={<ConciergeOSShell />}>
                                <Route index element={<ControlRoom />} />
                                {/* Operate */}
                                <Route path="operate/requests" element={<RequestsQueue />} />
                                <Route path="operate/anything" element={<AnythingQueue />} />
                                <Route path="operate/goldenbook" element={<GoldenBookQueue />} />
                                <Route path="operate/goldenbook-notes" element={<GoldenBookNotes />} />
                                <Route path="operate/fyi" element={<FyiQueue />} />
                                <Route path="operate/publish-gate" element={<PublishGate />} />
                                <Route path="operate/catalogue/enjoy-signature" element={<EnjoySignatureCRUD />} />
                                <Route path="operate/catalogue/enjoy-overlay" element={<EnjoyOverlayCRUD />} />
                                <Route path="operate/catalogue/table-signature" element={<TableSignatureCRUD />} />
                                <Route path="operate/catalogue/escape-signature" element={<EscapeSignatureCRUD />} />
                                <Route path="operate/mira" element={<MiraInboxPage />} />
                                <Route path="operate/enquiries" element={<EnquiriesPage />} />
                                {/* Modules */}
                                <Route path="modules/table" element={<TableModule />} />
                                <Route path="modules/enjoy" element={<EnjoyModule />} />
                                <Route path="modules/escape" element={<EscapeModule />} />
                                <Route path="modules/club" element={<ClubModule />} />
                                <Route path="modules/stay" element={<StayModule />} />
                                <Route path="modules/fit" element={<FitModule />} />
                                <Route path="modules/airport" element={<AirportModule />} />
                                <Route path="modules/mobility" element={<MobilityModule />} />
                                <Route path="modules/exclusive" element={<ExclusiveModule />} />
                                <Route path="secrets/export" element={<SecretsExportPage />} />
                                {/* Quality & Safety */}
                                <Route path="quality/media" element={
                                    <Placeholder
                                        title="Media Library"
                                        description="Uploaded · Cloudinary · Places · provider images · placeholders · rights audit."
                                        willInclude={[
                                            "image_url · source_type · source_url · credit · license_status · attribution_required · alt_text",
                                            "uploaded_by/at · used_on · status",
                                            "No blank live cards · no monograms on premium cards",
                                        ]}
                                        testid="os-placeholder-media"
                                    />
                                } />
                                <Route path="quality/source-audit" element={
                                    <Placeholder
                                        title="Source · Link Audit"
                                        description="Track source · official · partner · booking · affiliate URLs · last checked · broken/redirected/404/stale."
                                        willInclude={[
                                            "Recheck · verify · flag · suppress-CTA",
                                            "Zero-fabrication discipline",
                                        ]}
                                        testid="os-placeholder-source-audit"
                                    />
                                } />
                                <Route path="quality/safety" element={
                                    <Placeholder
                                        title="Safety & Risk Admin"
                                        description="Rows needing safety review · high-risk · age · accessibility · visa · medical · remote flags."
                                        willInclude={[
                                            "Approve · block-publish · escalate · classify",
                                            "high+extreme force human_required=true (ENFORCED)",
                                        ]}
                                        testid="os-placeholder-safety"
                                    />
                                } />
                                <Route path="quality/imports" element={
                                    <Placeholder
                                        title="Bulk Import Centre"
                                        description="Upload → detect sheets → map columns → validate → preview → import as DRAFT → batch id → rollback."
                                        willInclude={[
                                            "Michelin · marquee events · Signature · venues · club partners",
                                            "Future gift / stay suppliers",
                                            "Never auto-publish · success/fail counts",
                                        ]}
                                        testid="os-placeholder-imports"
                                    />
                                } />
                                {/* Operations */}
                                <Route path="operations/providers" element={
                                    <Placeholder
                                        title="Provider Registry"
                                        description="Provider · surface · API · status · last success/fail · credentials present (NEVER the key) · attribution · booking model."
                                        willInclude={[
                                            "Test connection · view logs · enable/disable",
                                            "Error logs · checkout ownership · rail · Zoho route",
                                            "Secrets shown as present yes/no only — never the value",
                                        ]}
                                        testid="os-placeholder-providers"
                                    />
                                } />
                                <Route path="operations/zoho" element={
                                    <Placeholder
                                        title="Zoho Control Centre"
                                        description="Action vs FYI tickets · failed sync · dedupe trail · retry queue · tag mapping."
                                        willInclude={[
                                            "Action and FYI views kept strictly separate",
                                            "Retry queue · failed sync resolution",
                                        ]}
                                        testid="os-placeholder-zoho"
                                    />
                                } />
                                <Route path="operations/members" element={<MembersPage />} />
                                <Route path="operations/aurelion-routing" element={<AurelionRoutingPage />} />
                                <Route path="operations/reports" element={
                                    <Placeholder
                                        title="Reports"
                                        description="Requests by surface/status · clicks by provider · broken links · image issues · top searched/clicked."
                                        willInclude={[
                                            "Pending human review · high-risk open · import quality · backup status",
                                            "NEVER label click-outs as completed bookings",
                                        ]}
                                        testid="os-placeholder-reports"
                                    />
                                } />
                                {/* System */}
                                <Route path="system/content" element={<SiteContentPage />} />
                                <Route path="system/doctrine" element={<DoctrineDocsPage />} />
                                <Route path="system/admin-urls" element={<AdminUrlsPage />} />
                                <Route path="system/webhook-log" element={<WebhookLogPage />} />
                                <Route path="system/health" element={
                                    <Placeholder
                                        title="System Health"
                                        description="SiteVault · provider API health · cron runs · test summary · failed jobs · env presence (yes/no, never the value)."
                                        willInclude={[
                                            "Cloudinary · Places · Zoho · Viator · Ticketmaster · PredictHQ health",
                                            "Collection counts · cron-run register · failed-job log",
                                            "Never displays secret values",
                                        ]}
                                        testid="os-placeholder-system-health"
                                    />
                                } />
                                <Route path="system/audit-log" element={
                                    <Placeholder
                                        title="Audit Log"
                                        description="Every admin action — user · action · module · entity · before/after · timestamp · IP hash · reason."
                                        willInclude={[
                                            "Reason required for delete/archive, publish high-risk, override safety, suppress provider result, force publish",
                                            "No silent edits",
                                        ]}
                                        testid="os-placeholder-audit-log"
                                    />
                                } />
                                <Route path="system/migration-map" element={<MigrationMap />} />
                                <Route path="system/sitevault" element={<SiteVaultDownloads />} />
                                <Route path="system/users" element={<RoleAdmin />} />
                            </Route>
                            {/* Escape hatch · the old flat Editorial-desk is
                                preserved at /admin/legacy until migration is
                                signed off. Same auth gate as before. */}
                            <Route path="/admin/legacy" element={<AdminShell><AdminDashboard /></AdminShell>} />
                            {/* Concierge® Console — separate dark surface */}
                            <Route path="/mira/console/login" element={<ConsoleShell><ConsoleLogin /></ConsoleShell>} />
                            <Route path="/mira/console" element={<ConsoleShell><ConsoleDashboard /></ConsoleShell>} />
                            {/* Club Social® — MindEscapes® partner-club portal · FIR-20260611-001 */}
                            <Route path="/club" element={<ClubDirectory />} />
                            <Route path="/club/signup" element={<ClubSignup />} />
                            <Route path="/club/admin" element={<ClubAdminClubsList />} />
                            <Route path="/club/admin/clubs" element={<ClubAdminClubsList />} />
                            <Route path="/club/admin/clubs/:id_short" element={<ClubAdminClubEdit />} />
                            <Route path="/club/admin/members" element={<ClubAdminMembersList />} />
                            <Route path="/club/admin/members/:card_no" element={<ClubAdminMemberEdit />} />
                            <Route path="/club/admin/requests" element={<ClubAdminRequests />} />
                            {/* ── Corporate Sponsor Tenant portal · 04 Jul 2026 ──
                                Employees at sponsoring companies land at
                                lesconcierge.com/{slug} · magic-link auth · LC-branded
                                dashboard mirroring the MC hero-then-Mira pattern.
                                Admin CRUD lives at /admin/corporate.  Reserved slugs
                                (table/stay/fit/admin/console/etc.) are enforced on
                                the backend so no company can collide with these
                                static routes. */}
                            <Route path="/admin/corporate" element={<AdminCorporateTenants />} />
                            <Route path="/:slug/sign-in"  element={<CorporateSignIn />} />
                            <Route path="/:slug/terms"    element={<CorporateTerms />} />
                            <Route path="/:slug/dashboard" element={<CorporateDashboard />} />
                            <Route path="/:slug"           element={<CorporateLanding />} />
                            <Route path="*" element={<PublicShell><Home /></PublicShell>} />
                        </Routes>
                    </BrowserRouter>
                    </ThemeProvider>
                </ConsoleAuthProvider>
            </AuthProvider>
        </ContentProvider>
    );
}

export default App;
